import { sexText } from "@patients/signalment";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import { formatClockTime } from "@scribenote/brian-design-system";
import { formatMonthDay } from "@utils/format-date";

import type { LastVisit, Owner, SheetHeader, Weight } from "./examRoomView";

export const joinDots = (...parts: (string | null | undefined)[]): string =>
  parts.filter(Boolean).join(" · ");

export const patientFullName = ({ name, surname }: SheetHeader): string => {
  const family = surname?.trim();
  if (!family || name.toLowerCase().endsWith(family.toLowerCase())) return name;
  return `${name} ${family}`;
};

// Mirrors brian `models/patient.py` `Sex` and `ReproductiveStatus`.
const SEX_CODE: Record<string, { intact: string; neutered: string }> = {
  female: { intact: "F", neutered: "FS" },
  male: { intact: "M", neutered: "MN" },
};

/** The mocks' "FS" / "MN" / "F"; words for values brian doesn't enumerate. */
const sexAbbreviation = (
  sex: string | null,
  reproductiveStatus: string | null,
): string | null => {
  const code = sex ? SEX_CODE[sex] : undefined;
  if (!code) return sexText(sex, reproductiveStatus);
  if (reproductiveStatus === "neutered") return code.neutered;
  if (reproductiveStatus === "intact" || !reproductiveStatus)
    return code.intact;
  return sexText(sex, reproductiveStatus);
};

/** "DLH · black · 4 y · FS"; breed stands in for species when known. */
export const signalmentLine = ({ signalment: s }: SheetHeader): string =>
  joinDots(
    s.breed ?? s.species,
    s.color,
    s.age,
    sexAbbreviation(s.sex, s.reproductiveStatus),
  );

const clock = (iso: string): string => formatClockTime(new Date(iso));

/** "booked 3:00 with Dr. Katie" */
export const bookingLine = ({
  scheduledStart,
  dvmName,
}: SheetHeader): string | null => {
  if (!scheduledStart) return dvmName;
  const booked = `booked ${clock(scheduledStart)}`;
  return dvmName ? `${booked} with ${dvmName}` : booked;
};

export const weightText = (w: Weight): string => `${w.value} ${w.unit}`;

/** "Tue Sep 15 · fluids 150 mL · 3.80 kg" */
export const lastVisitLine = (visit: LastVisit): string => {
  const date = new Date(visit.date);
  const weekday = date.toLocaleDateString([], { weekday: "short" });
  return joinDots(
    `${weekday} ${formatMonthDay(visit.date)}`,
    visit.summary,
    visit.weight && weightText(visit.weight),
  );
};

/** "Claire Petit · (613) 555-0164 · claire.petit@gmail.com" */
export const ownerLine = (owner: Owner): string =>
  joinDots(
    owner.name,
    owner.phone && formatPhoneNumber(owner.phone),
    owner.email,
  );

/** "in Treatment since 2:33", or "out of the room since 2:33" when in no room. */
export const awayLine = (
  awaySince: string | null,
  awayIn: string | null,
): string | null =>
  awaySince &&
  `${awayIn ? `in ${awayIn}` : "out of the room"} since ${clock(awaySince)}`;
